import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import {
  PhrasesTextElement,
  PhrasesStickerElement,
  PhrasesResourceElement,
  PhraseAttachmentElement,
} from 'services/api/phrase';
import useFormatShortKey from 'views/Setting/Phrase/hooks/useFormatShortKey';
import {
  useMemo,
  forwardRef,
  useRef,
  useImperativeHandle,
  Ref,
  useLayoutEffect,
  useState,
} from 'react';
import { useMemoizedFn } from 'ahooks';
import { isUndefined } from 'lodash-es';
import { PhraseValue } from 'hooks/business/usePhraseOnChat';
import EnhanceText from 'components/Dialogue/components/Message/EnhanceText';
import { ReactComponent as SendSvg } from './assets/send.svg';
import { ReactComponent as EditSvg } from './assets/edit.svg';
import { Mode } from 'hooks/business/usePhraseDisplayMode';
import { useTranslate } from '@tolgee/react';

const Container = styled.div<{ sideMode?: boolean }>`
  display: flex;
  padding: 4px;
  border-radius: 4px;
  color: #454d5a;
  border: solid 2px #fff;
  &.active {
    background-color: var(--fett-erp-card-bg);
    border-color: ${({ sideMode }) => (sideMode ? '#fff' : '#f7f9fa')};
  }
  > span {
    flex: 0 0 auto;
  }
  > div {
    flex: 1 0 0;
    margin: 0 4px;
    > p {
      margin: 0;
    }
    > section {
      margin: 4px 0;
      border-top: dashed 1px #cbced1;
    }
  }
  > section {
    display: flex;
    align-items: center;
    display: none;
    > * {
      margin-left: 8px;
    }
  }
  &:hover {
    > section {
      display: inline;
    }
  }
`;

const Shortcut = styled.div`
  display: inline-block;
  line-height: 22px;
  margin: 1px 4px;
  padding: 0 4px;
  color: var(--ant-primary-color);
  border: solid 1px #cbced1;
  border-radius: 2px;
`;

const Tag = styled.div`
  display: inline-block;
  line-height: 21px;
  margin: 1px 4px;
  padding: 0 4px;
  color: var(--ant-primary-color);
  background: var(--ant-primary-2);
  border-radius: 2px;
`;

export interface PhraseItemProps {
  index?: number;
  data: PhraseValue;
  active?: boolean;
  mode?: Mode;
  mercadoQAmode?: boolean;
  sideMode?: boolean;
  onEditClick?: (text: PhrasesTextElement) => void;
  onSendClick?: (data: PhraseValue) => void;
  onMouseMove?: () => void;
  onMouseLeave?: () => void;
}

export interface PhraseItemRef {
  scrollTo: () => void;
}

export const getPhraseTextElement = (data: PhraseValue) => {
  const { phraseElems } = data;
  const text = phraseElems?.elems?.find?.((elem) => (elem as PhrasesTextElement)?.text);
  return {
    text: text as PhrasesTextElement | undefined,
    onlyText: !isUndefined(text) && phraseElems?.elems.length === 1,
  };
};

const PhraseItemImplement = (props: PhraseItemProps, ref: Ref<PhraseItemRef>) => {
  const {
    index,
    data,
    active,
    mode,
    mercadoQAmode = false,
    sideMode = false,
    onMouseMove,
    onEditClick,
    onSendClick,
    onMouseLeave,
  } = props;
  const formatShortKey = useFormatShortKey();
  const { t } = useTranslate();

  const elRef = useRef<HTMLDivElement | null>(null);

  const { text: textEl, onlyText: onlyTextEl } = useMemo(() => {
    return getPhraseTextElement(data);
  }, [data]);

  const renderEl = useMemo(() => {
    let images = 0;
    let sticker = 0;
    let videos = 0;
    let attachments = 0;
    let textContent = '';
    let textTranslateContent = '';

    data?.phraseElems.elems.forEach((el) => {
      if ((el as PhrasesTextElement).text) {
        const { text } = el as PhrasesTextElement;
        textContent = text.content;
        textTranslateContent = text.translationContent;
      }
      if ((el as PhrasesStickerElement).sticker) {
        sticker += 1;
      }

      if ((el as PhrasesResourceElement).resource) {
        const { resource } = el as PhrasesResourceElement;
        if (resource.resourceType === 1) {
          images += 1;
        }
        if (resource.resourceType === 2) {
          videos += 1;
        }
      }
      if ((el as PhraseAttachmentElement).attachment) {
        attachments += (el as PhraseAttachmentElement).attachment.attachments.length;
      }
    });

    return {
      images,
      sticker,
      videos,
      attachments,
      textContent,
      textTranslateContent,
    };
  }, [data]);

  const handleSend = useMemoizedFn((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    onSendClick?.(data);
  });

  useImperativeHandle(ref, () => ({
    scrollTo: () => {
      elRef.current?.scrollIntoView(false);
    },
  }));

  const shortcut = data.shortcutKey ? (
    <Shortcut>{formatShortKey(data.shortcutKey)}</Shortcut>
  ) : null;
  const tags = data.tags.length ? data.tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : null;

  const tagRef = useRef<HTMLSpanElement>(null);
  const tag = (shortcut || tags) && (
    <span ref={tagRef}>
      {shortcut}
      {tags}
    </span>
  );

  const [breakLine, setBreakLine] = useState(sideMode);

  useLayoutEffect(() => {
    if (!sideMode && tagRef.current && tagRef.current.parentElement) {
      setBreakLine(
        tagRef.current.getBoundingClientRect().width /
          tagRef.current.parentElement.getBoundingClientRect().width >
          0.5,
      );
    }
  }, [sideMode]);

  const text1 = (
    <EnhanceText
      text={
        mode === 'onlyOriginal'
          ? renderEl.textContent
          : renderEl.textTranslateContent || renderEl.textContent
      }
    />
  );
  const text2 =
    mode === 'all' && renderEl.textTranslateContent ? (
      <EnhanceText text={renderEl.textContent} />
    ) : null;

  return (
    <Container
      ref={elRef}
      className={active ? 'active' : ''}
      sideMode={sideMode}
      onMouseMove={(e) => {
        e.stopPropagation();
        onMouseMove?.();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
    >
      {index !== undefined && <span>{index}.</span>}
      <div>
        {breakLine && tag ? (
          <>
            {tag}
            <div className="h-1" />
            <Tooltip title={text1}>
              <p className={sideMode ? 'line-clamp-2' : 'line-clamp-1'}>{text1}</p>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={text1}>
            <p className={sideMode ? 'line-clamp-2' : 'line-clamp-1'}>
              {tag}
              {text1}
            </p>
          </Tooltip>
        )}
        {text2 && <section />}
        {text2 && (
          <Tooltip title={text2}>
            <p className={sideMode ? 'line-clamp-2' : 'line-clamp-1'}>{text2}</p>
          </Tooltip>
        )}
      </div>
      <section>
        {textEl && onlyTextEl && !mercadoQAmode && (
          <Tooltip title={t('scene.chatWorkspace.quickReply.action.sendAfterEditing')}>
            <Icon
              component={EditSvg}
              onClick={(e) => {
                e.stopPropagation();
                onEditClick?.(textEl);
              }}
            />
          </Tooltip>
        )}
        <Tooltip title={mercadoQAmode ? t('reply.应用') : t('common.action.send')}>
          <Icon component={SendSvg} onClick={handleSend} />
        </Tooltip>
      </section>
    </Container>
  );
};

export default forwardRef(PhraseItemImplement);
